import React, { useRef, useState, useEffect, useCallback } from "react";

// Helper function to extract main domain from hostname
// e.g., "affiliate.baji10x.com" -> "baji10x.com"
const getMainDomain = (hostname) => {
  const parts = hostname.split('.');
  // If it's an IP address or localhost, return as-is
  if (parts.length <= 2 || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    return hostname;
  }
  // Return the last two parts (main domain + TLD)
  return parts.slice(-2).join('.');
};

function ChatTongole() {
  const chatRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [logo, setLogo] = useState("");
  const [whatsappLink, setWhatsappLink] = useState(null);
  const [dragDistance, setDragDistance] = useState(0);
  const [liveChatData, setLiveChatData] = useState(null);

  const [position, setPosition] = useState({
    x: window.innerWidth - 70,
    y: window.innerHeight - 150,
  });

  const [offset, setOffset] = useState({ x: 0, y: 0 });

  // Fetch logo
  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/logo`);
        if (!response.ok) throw new Error("Failed to fetch logo");
        const data = await response.json();
        setLogo(`${process.env.REACT_APP_API_URL}/storage/${data[0].mobile_logo}`);
      } catch (error) {
        console.error("Error fetching logo:", error);
      }
    };

    fetchLogo();
  }, []);

  // Fetch live chat data from API server filtered by main domain
  useEffect(() => {
    const fetchLiveChatData = async () => {
      try {
        const hostname = window.location.hostname;
        const mainDomain = getMainDomain(hostname);
        const mainOrigin = `https://www.${mainDomain}`;
        
        console.log("Fetching live chat data for origin:", mainOrigin);
        
        // Try GET request with origin as query param
        const response = await fetch(`https://api2b.pro/api/general?origin=${encodeURIComponent(mainOrigin)}`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          },
        });
        
        if (!response.ok) {
          console.log("Response status:", response.status);
          throw new Error("Failed to fetch general data");
        }
        
        let data = await response.json();
        console.log("Live chat data received:", data);
		data = data[0] ?? [];
        
        setLiveChatData(data);
        
        // Store live chat related data in localStorage
        if (data.live_chat) {
          localStorage.setItem("liveChat", data.live_chat);
        }
        if (data.whatsapp) {
          localStorage.setItem("whatsapp", data.whatsapp);
          setWhatsappLink(data.whatsapp);
        }
        if (data.intercom) {
          localStorage.setItem("intercom", data.intercom);
        }
        
        // Initialize live chat widget if live_chat script URL is available
        if (data.live_chat && data.live_chat.length > 0) {
          // If live_chat contains a script URL or license number
          if (data.live_chat.includes('livechatinc') || /^\d+$/.test(data.live_chat)) {
            const licenseId = /^\d+$/.test(data.live_chat) ? parseInt(data.live_chat) : null;
            if (licenseId) {
              window.__lc = window.__lc || {};
              window.__lc.license = licenseId;
            }

            (function (n, t, c) {
              function i(n) {
                return e._h ? e._h.apply(null, n) : e._q.push(n);
              }
              var e = {
                _q: [],
                _h: null,
                _v: "2.0",
                on: function () {
                  i(["on", c.call(arguments)]);
                },
                once: function () {
                  i(["once", c.call(arguments)]);
                },
                off: function () {
                  i(["off", c.call(arguments)]);
                },
                get: function () {
                  if (!e._h)
                    throw new Error("[LiveChatWidget] You can't use getters before load.");
                  return i(["get", c.call(arguments)]);
                },
                call: function () {
                  i(["call", c.call(arguments)]);
                },
                init: function () {
                  var n = t.createElement("script");
                  n.async = !0;
                  n.type = "text/javascript";
                  n.src = "https://cdn.livechatinc.com/tracking.js";
                  t.head.appendChild(n);
                },
              };
              !n.__lc.asyncInit && e.init();
              n.LiveChatWidget = n.LiveChatWidget || e;
            })(window, document, [].slice);
          }
        }
        
        // Load Tawk.to if available (API returns tawk_id)
        if (data.tawk_id) {
          // tawk_id format: widgetId/propertyId e.g., "abc123/def456"
          var Tawk_API = window.Tawk_API || {};
          // eslint-disable-next-line no-unused-vars
          var Tawk_LoadStart = new Date();
          (function(){
            var s1 = document.createElement("script");
            s1.async = true;
            s1.src = `https://embed.tawk.to/${data.tawk_id}`;
            s1.charset = "UTF-8";
            s1.setAttribute("crossorigin", "*");
            var s0 = document.getElementsByTagName("script")[0];
            s0.parentNode.insertBefore(s1, s0);
          })();
          window.Tawk_API = Tawk_API;
        }
        
        // Load Intercom if available
        if (data.intercom) {
          window.intercomSettings = {
            api_base: "https://api-iam.intercom.io",
            app_id: data.intercom,
          };
          (function(){var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic('reattach_activator');ic('update',w.intercomSettings);}else{var d=document;var i=function(){i.c(arguments);};i.q=[];i.c=function(args){i.q.push(args);};w.Intercom=i;var l=function(){var s=d.createElement('script');s.type='text/javascript';s.async=true;s.src='https://widget.intercom.io/widget/' + data.intercom;var x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s,x);};if(document.readyState==='complete'){l();}else if(w.attachEvent){w.attachEvent('onload',l);}else{w.addEventListener('load',l,false);}}})();
        }
        
      } catch (error) {
        console.error("Error fetching live chat data:", error);
        // Fallback to localStorage values
        const whatsapp = localStorage.getItem("whatsapp");
        if (whatsapp && whatsapp !== 'null') {
          setWhatsappLink(whatsapp);
        }
      }
    };

    fetchLiveChatData();
  }, []);

  // eslint-disable-next-line no-unused-vars
	const [show, setShow] = useState(null);
	useEffect(() => {
		let whatsapp = localStorage.getItem("whatsapp");
		let live_chat = localStorage.getItem("liveChat");
		// let intercom = localStorage.getItem("intercom");
		if (live_chat!=='null') {
			setShow('live_chat');
		}else {
			setShow('base_chat');
		}
		setWhatsappLink((whatsapp!=='null') ? whatsapp : null);
	}, [liveChatData]);

 

  // Drag Start
  const handleStart = (e) => {
    setDragging(true);
    setDragDistance(0); // Reset drag distance at the start

    const event = e.type === "touchstart" ? e.touches[0] : e;
    const rect = chatRef.current.getBoundingClientRect();

    setOffset({
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    });

    // Add global event listeners
    if (e.type === "mousedown") {
      document.addEventListener("mousemove", handleMove);
      document.addEventListener("mouseup", handleEnd);
    } else if (e.type === "touchstart") {
      document.addEventListener("touchmove", handleMove, { passive: false });
      document.addEventListener("touchend", handleEnd);
    }
  };

  const handleMove = (e) => {
    if (!dragging) return;

    const event = e.type === "touchmove" ? e.touches[0] : e;
    e.preventDefault(); // Prevent scrolling on touch devices

    const newX = event.clientX - offset.x;
    const newY = event.clientY - offset.y;

    // Update the drag distance
    const distance = Math.sqrt(
      Math.pow(newX - position.x, 2) + Math.pow(newY - position.y, 2)
    );
    setDragDistance(distance);

    setPosition({ x: newX, y: newY });
  };

  const handleEnd = (e) => {
    // Prevent link click if dragging occurred
    if (dragDistance > 10) {
      e.preventDefault();
    }

    setDragging(true);

    // Remove global event listeners
    document.removeEventListener("mousemove", handleMove);
    document.removeEventListener("mouseup", handleEnd);
    document.removeEventListener("touchmove", handleMove);
    document.removeEventListener("touchend", handleEnd);
	};

	const handleChatClick = useCallback((e) => {
		console.log("Chat Clicked!");
		if (whatsappLink == null || whatsappLink.length === 0) { 
			let wan = localStorage.getItem("whatsapp")!== 'null' ? false : localStorage.getItem("whatsapp");
			setWhatsappLink(wan ?? false);
		}
		console.log("wa:",whatsappLink);
		if (e) {
			e.preventDefault();
		}

		const remoteId = localStorage.getItem("remote_id");
		const username = localStorage.getItem("username");
		
		if (window.Intercom) {
			window.Intercom("show");
		}else if (whatsappLink && whatsappLink!=null && whatsappLink !== 'null' && whatsappLink.length > 0) {
			console.log("WhatsApp Link:", whatsappLink);
			window.open(whatsappLink, "_blank");
		} else {
			if (window?.anw2 !== undefined) {
				console.log('anw2');
				window.anw2.open();
			}
			if (window.LiveChatWidget) {
				console.log('lch');
				window.LiveChatWidget.once("ready", () => {
					if (remoteId && username) {
						window.LiveChatWidget.call("set_customer_data", {
							name: username,
							external_id: remoteId, 
						});
					}
					window.LiveChatWidget.call("maximize");
				});
			}
			if (window.Tawk_API) {
				window.Tawk_API.maximize();
			}
		}
  	}, [whatsappLink]);
	
	// Listen for the custom event to open chat
	useEffect(() => {
		const handler = () => handleChatClick();
		window.addEventListener('open_chat', handler);
		return () => window.removeEventListener('open_chat', handler);
	}, [handleChatClick]);
  
	

	return (
	  <>
		<style>{`
			[id^="anw2-launcher-container-"] [id^="anw2-launcher-"]{
				display: none !important;
			}
		`}</style>
	  
    	<button type="button" onClick={handleChatClick} style={{ background: 'none', border: 'none', padding: 0 }}>
			<div
				ref={chatRef}
				className="chat-tongole"
				style={{
				position: "fixed",
				left: `${position.x}px`,
				top: `${position.y}px`,
				cursor: dragging ? "grabbing" : "grab",
        zIndex: 2147483647,
        pointerEvents: "auto",
				width: "50px",
				height: "50px",
				borderRadius: "50%",
				backgroundColor: "rgb(255 138 20)",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
				}}
				onMouseDown={handleStart}
				onTouchStart={handleStart} >
				<img src={logo} alt="Chat Icon" style={{ width: "26px" }} />
			</div>
		</button>
	</>
  );
}

export default ChatTongole;
